import type { JobItemDto } from "@artha/api-contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type RestrictionState,
  getJobCountryForRestriction,
  getViewerGeo,
  isCountryMismatch,
  isGeoRestrictionDisabled,
  isIncognitoActive,
  isVpnActive,
} from "../services/geolocationRestriction";
import { useAudienceAnalytics } from "@/lib/analytics";
import { authService } from "@/lib/authService";

type UseApplyRestrictionsOptions = {
  enabled?: boolean;
  job: JobItemDto | null;
  suppressForAuthenticatedCreator?: boolean;
  creatorUsername?: string;
  creator?: unknown;
};

export const useApplyRestrictions = ({
  enabled = true,
  job,
  suppressForAuthenticatedCreator = true,
  creatorUsername,
  creator,
}: UseApplyRestrictionsOptions) => {
  const [restriction, setRestriction] = useState<RestrictionState>({
    isRestrictedByCountry: false,
    isRestrictedByVpn: false,
    isRestrictedByIncognito: false,
  });

  const [isCountryModalOpen, setIsCountryModalOpen] = useState(false);
  const [isVpnModalOpen, setIsVpnModalOpen] = useState(false);
  const [isIncognitoModalOpen, setIsIncognitoModalOpen] = useState(false);

  const isAuthenticatedCreator =
    suppressForAuthenticatedCreator && authService.isAuthenticated();

  const jobCountry = useMemo(
    () => (job ? getJobCountryForRestriction(job) : null),
    [job]
  );

  const shouldCheck = enabled && !isAuthenticatedCreator && !isGeoRestrictionDisabled();

  const { trackVpnRestrictionShown, trackCountryRestrictionShown, trackIncognitoRestrictionShown } =
    useAudienceAnalytics(creatorUsername);

  // Keep creator in a ref so it never causes checkRestriction to be recreated
  // (creator is only used for analytics props, not restriction logic)
  const creatorRef = useRef(creator);
  useEffect(() => {
    creatorRef.current = creator;
  });

  const checkRestriction = useCallback(async (guard: { active: boolean }) => {
    console.debug("[GeoRestriction] check started", { shouldCheck, isAuthenticatedCreator, enabled, jobCountry: jobCountry?.code });

    const restrictedByIncognito = enabled && !isGeoRestrictionDisabled() ? await isIncognitoActive() : false;

    if (!guard.active) {
      console.debug("[GeoRestriction] cancelled (guard inactive)");
      return;
    }

    if (restrictedByIncognito) {
      console.debug("[GeoRestriction] restricted by incognito");
      setRestriction({
        isRestrictedByCountry: false,
        isRestrictedByVpn: false,
        isRestrictedByIncognito: true,
        jobCountry: jobCountry ?? undefined,
      });
      setIsIncognitoModalOpen(true);
      setIsCountryModalOpen(false);
      setIsVpnModalOpen(false);
      console.debug("[GeoRestriction] firing AUD_INCOGNITO_RESTRICTION_SHOWN");
      trackIncognitoRestrictionShown(job, creatorRef.current);
      return;
    } else {
      setIsIncognitoModalOpen(false);
    }

    if (!shouldCheck) {
      console.debug("[GeoRestriction] skipped — shouldCheck is false", { enabled, isAuthenticatedCreator });
      setRestriction({
        isRestrictedByCountry: false,
        isRestrictedByVpn: false,
        isRestrictedByIncognito: false,
        jobCountry: jobCountry ?? undefined,
      });
      setIsCountryModalOpen(false);
      setIsVpnModalOpen(false);
      return;
    }

    const geo = await getViewerGeo();
    console.debug("[GeoRestriction] geo result", geo);

    if (!guard.active) {
      console.debug("[GeoRestriction] cancelled (guard inactive)");
      return;
    }

    const seekerCountry = geo?.country;
    const ipTimezone = geo?.timezone || "unknown";
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const isCampaign = () => {
      if (typeof window === "undefined") return false;
      const params = new URLSearchParams(window.location.search);
      const campaignKeys = [
        "utm_source",
        "utm_target",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "_bhlid",
        "gclid",
        "fbclid",
      ];
      return campaignKeys.some((key) => params.has(key));
    };

    const restrictedByCountry =
      jobCountry ? isCountryMismatch(seekerCountry, jobCountry) : false;
    const restrictedByVpn =
      !restrictedByCountry &&
      !isCampaign() &&
      (geo?.isVpn || geo?.isProxy || geo?.isHosting || isVpnActive(seekerCountry?.code, browserTimezone));

    console.debug("[GeoRestriction] result", {
      seekerCountry: seekerCountry?.code,
      jobCountry: jobCountry?.code,
      ipTimezone,
      browserTimezone,
      restrictedByCountry,
      restrictedByVpn,
    });

    setRestriction({
      isRestrictedByCountry: restrictedByCountry,
      isRestrictedByVpn: restrictedByVpn,
      isRestrictedByIncognito: false,
      seekerCountry: seekerCountry ?? undefined,
      jobCountry: jobCountry ?? undefined,
    });

    if (restrictedByCountry) {
      setIsCountryModalOpen(true);
      if (jobCountry && job) {
        console.debug("[GeoRestriction] firing AUD_COUNTRY_RESTRICTION_SHOWN");
        trackCountryRestrictionShown(
          seekerCountry?.code || "unknown",
          jobCountry.code,
          job,
          creatorRef.current
        );
      }
    } else {
      setIsCountryModalOpen(false);
    }

    if (restrictedByVpn) {
      setIsVpnModalOpen(true);
      console.debug("[GeoRestriction] firing AUD_VPN_RESTRICTION_SHOWN");
      trackVpnRestrictionShown(
        ipTimezone,
        browserTimezone,
        job,
        creatorRef.current
      );
    } else {
      setIsVpnModalOpen(false);
    }
  }, [
    enabled,
    isAuthenticatedCreator,
    jobCountry,
    shouldCheck,
    trackVpnRestrictionShown,
    trackCountryRestrictionShown,
    trackIncognitoRestrictionShown,
    job,
  ]);

  useEffect(() => {
    const guard = { active: true };
    queueMicrotask(() => {
      void checkRestriction(guard);
    });

    // Re-check on window focus (e.g. user turned off VPN and came back)
    const handleFocus = () => {
      void checkRestriction(guard);
    };
    window.addEventListener("focus", handleFocus);

    return () => {
      guard.active = false;
      window.removeEventListener("focus", handleFocus);
    };
  }, [checkRestriction]);

  const blockIfRestricted = () => {
    if (restriction.isRestrictedByIncognito) {
      setIsIncognitoModalOpen(true);
      return true;
    }
    if (restriction.isRestrictedByCountry) {
      setIsCountryModalOpen(true);
      return true;
    }
    if (restriction.isRestrictedByVpn) {
      setIsVpnModalOpen(true);
      return true;
    }
    return false;
  };

  return {
    restriction,
    isCountryModalOpen,
    closeCountryModal: () => setIsCountryModalOpen(false),
    isVpnModalOpen,
    closeVpnModal: () => setIsVpnModalOpen(false),
    isIncognitoModalOpen,
    closeIncognitoModal: () => setIsIncognitoModalOpen(false),
    blockIfRestricted,
  };
};
